import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import {
  FileText,
  Search,
  // Filter,
  // CheckCircle,
  // XCircle,
  Clock,
  Eye,
  // Check,
  UserCheck,
  // Briefcase,
  // Calendar,
  Phone,
  Mail,
  // MapPin,
  // Award,
  // BookOpen,
  // ArrowRight,
  ChevronLeft,
  ChevronRight,
  // ThumbsUp,
  AlertCircle
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  SimpleGrid,
  Box,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Divider,
  Spinner,
  Textarea,
  Tabs,
  TabList,
  Tab,
  Stat,
  StatLabel,
  StatNumber,
  List,
  ListItem,
  ListIcon,
  Tag,
  TagLabel,
  Grid,
  GridItem
} from '@chakra-ui/react'

// Application interface matching backend
interface DriverApplication {
  id: string
  applicantId: string
  jobPostingId?: string
  roleAppliedFor: 'DRIVER' | 'CONDUCTOR'
  dateOfBirth: string
  gender: string
  address: string
  yearsOfExperience: number
  licenseNumber?: string
  licenseExpiry?: string
  licenseClasses?: string[]
  nationalIdUrl?: string
  passportUrl?: string
  cvUrl?: string
  licenseUrl?: string
  status: 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'HIRED' | 'WITHDRAWN'
  createdAt: string
  updatedAt: string
  reviewedById?: string
  reviewedAt?: string
  reviewNotes?: string
  rejectionReason?: string
  applicant?: {
    id: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
  }
  recommenders?: Array<{
    id: string
    fullName: string
    relationship: string
    phoneNumber: string
    email?: string
    organization?: string
  }>
  jobPosting?: {
    id: string
    title: string
  }
}

export default function ApplicationsPage() {
  const queryClient = useQueryClient()
  const toast = useToast()

  // State for filters & pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [roleFilter, setRoleFilter] = useState<string>('ALL')
  const [searchTerm, setSearchTerm] = useState('')

  // Modals state
  const [selectedApp, setSelectedApp] = useState<DriverApplication | null>(null)
  const [isViewOpen, setIsViewOpen] = useState(false)

  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const [reviewStatus, setReviewStatus] = useState<'UNDER_REVIEW' | 'APPROVED' | 'REJECTED'>('UNDER_REVIEW')
  const [reviewNotes, setReviewNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')

  const [isHireOpen, setIsHireOpen] = useState(false)
  const [assignedBusId, setAssignedBusId] = useState('')

  // Fetch applications
  const { data: appsData, isLoading: isAppsLoading, error: appsError } = useQuery(
    ['applications', currentPage, statusFilter, roleFilter],
    () => cmsService.getApplications({
      page: currentPage,
      limit: 10,
      status: statusFilter === 'ALL' ? undefined : (statusFilter as any),
      roleAppliedFor: roleFilter === 'ALL' ? undefined : (roleFilter as any)
    }),
    {
      keepPreviousData: true
    }
  )

  // Fetch vehicles (for hiring dialog)
  const { data: vehiclesData } = useQuery(
    'vehicles',
    () => cmsService.getVehicles({ limit: 100 }),
    {
      enabled: isHireOpen
    }
  )

  // Fetch stats (we can calculate from total applications, or run a query. For simplicity, let's load all to calculate stats, or fetch with pagination)
  const { data: allAppsStats } = useQuery(
    ['applications-stats'],
    () => cmsService.getApplications({ limit: 1000 })
  )

  // Mutations
  const reviewMutation = useMutation(
    ({ id, status, reviewNotes, rejectionReason }: { id: string, status: 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED', reviewNotes?: string, rejectionReason?: string }) =>
      cmsService.reviewApplication(id, { status, reviewNotes, rejectionReason }),
    {
      onSuccess: (data) => {
        toast({
          title: 'Review Submitted',
          description: `Application status has been updated to ${data.data.status}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        queryClient.invalidateQueries('applications')
        queryClient.invalidateQueries('applications-stats')
        setIsReviewOpen(false)
        setReviewNotes('')
        setRejectionReason('')
        // Refresh detail view if open
        if (selectedApp?.id === data.data.id) {
          setSelectedApp({ ...selectedApp, ...data.data })
        }
      }
    }
  )

  const hireMutation = useMutation(
    ({ id, hiredRole, assignedBusId }: { id: string, hiredRole: 'DRIVER' | 'CONDUCTOR', assignedBusId: string }) =>
      cmsService.hireApplicant(id, { hiredRole, assignedBusId }),
    {
      onSuccess: (data) => {
        toast({
          title: 'Applicant Hired 🎉',
          description: 'The applicant is now registered as an active crew member.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        })
        queryClient.invalidateQueries('applications')
        queryClient.invalidateQueries('applications-stats')
        setIsHireOpen(false)
        setAssignedBusId('')
        if (selectedApp?.id === data.data.id) {
          setSelectedApp({ ...selectedApp, ...data.data })
        }
      }
    }
  )

  // Calculate stats from allAppsStats
  const appsList = allAppsStats?.applications || []
  const stats = {
    total: appsList.length,
    submitted: appsList.filter((a: any) => a.status === 'SUBMITTED').length,
    underReview: appsList.filter((a: any) => a.status === 'UNDER_REVIEW').length,
    approved: appsList.filter((a: any) => a.status === 'APPROVED').length,
    hired: appsList.filter((a: any) => a.status === 'HIRED').length,
    rejected: appsList.filter((a: any) => a.status === 'REJECTED').length,
  }

  // Filter application list on client-side for search
  const displayedApps = appsData?.applications || []
  const filteredApps = displayedApps.filter((app: DriverApplication) => {
    const fullName = `${app.applicant?.firstName || ''} ${app.applicant?.lastName || ''}`.toLowerCase()
    const email = (app.applicant?.email || '').toLowerCase()
    const phone = (app.applicant?.phoneNumber || '').toLowerCase()
    const match = fullName.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase()) || phone.includes(searchTerm.toLowerCase())
    return match
  })

  // Theme values
  const bg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { color: string; label: string }> = {
      DRAFT: { color: 'gray', label: 'Draft' },
      SUBMITTED: { color: 'blue', label: 'Pending Review' },
      UNDER_REVIEW: { color: 'purple', label: 'Under Review' },
      APPROVED: { color: 'teal', label: 'Approved' },
      REJECTED: { color: 'red', label: 'Rejected' },
      HIRED: { color: 'green', label: 'Hired' },
      WITHDRAWN: { color: 'orange', label: 'Withdrawn' },
    }
    const config = configs[status] || { color: 'gray', label: status }
    return <Badge colorScheme={config.color} variant="solid" px={2} py={1} borderRadius="full">{config.label}</Badge>
  }

  const handleOpenView = async (app: DriverApplication) => {
    try {
      const fullApp = await cmsService.getApplicationById(app.id)
      setSelectedApp(fullApp.data)
      setIsViewOpen(true)
    } catch (err) {
      toast({
        title: 'Error loading details',
        description: 'Could not fetch full application details.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const handleOpenReview = (app: DriverApplication) => {
    setSelectedApp(app)
    setReviewStatus(app.status === 'SUBMITTED' ? 'UNDER_REVIEW' : (app.status as any))
    setReviewNotes(app.reviewNotes || '')
    setRejectionReason(app.rejectionReason || '')
    setIsReviewOpen(true)
  }

  const handleOpenHire = (app: DriverApplication) => {
    setSelectedApp(app)
    setAssignedBusId('')
    setIsHireOpen(true)
  }

  const handleReviewSubmit = () => {
    if (!selectedApp) return
    reviewMutation.mutate({
      id: selectedApp.id,
      status: reviewStatus,
      reviewNotes,
      rejectionReason: reviewStatus === 'REJECTED' ? rejectionReason : undefined
    })
  }

  const handleHireSubmit = () => {
    if (!selectedApp || !assignedBusId) return
    hireMutation.mutate({
      id: selectedApp.id,
      hiredRole: selectedApp.roleAppliedFor,
      assignedBusId
    })
  }

  const totalPages = appsData?.pagination?.pages || 1

  return (
    <Box p={6} maxW="7xl" mx="auto">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8} wrap="wrap" gap={4}>
        <VStack align="start" spacing={1}>
          <Heading size="lg" fontWeight="bold" letterSpacing="tight">Driver & Conductor Applications</Heading>
          <Text color="gray.500">Review driver credentials, process assessments, and onboard new crew members.</Text>
        </VStack>
      </Flex>

      {/* Stats Bar */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={8}>
        <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
          <CardBody p={4}>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">Total Applied</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold">{stats.total}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
          <CardBody p={4}>
            <Stat>
              <StatLabel color="blue.500" fontSize="sm">Pending Review</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color="blue.600">{stats.submitted}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
          <CardBody p={4}>
            <Stat>
              <StatLabel color="purple.500" fontSize="sm">Under Review</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color="purple.600">{stats.underReview}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
          <CardBody p={4}>
            <Stat>
              <StatLabel color="teal.500" fontSize="sm">Approved</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color="teal.600">{stats.approved}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
          <CardBody p={4}>
            <Stat>
              <StatLabel color="green.500" fontSize="sm">Hired Crew</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color="green.600">{stats.hired}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
          <CardBody p={4}>
            <Stat>
              <StatLabel color="red.500" fontSize="sm">Rejected</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color="red.600">{stats.rejected}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filters and Search */}
      <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" mb={6}>
        <CardBody p={4}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={4} justify="space-between" align="center">
            {/* Search */}
            <InputGroup maxW={{ base: 'full', md: '320px' }}>
              <InputLeftElement pointerEvents="none">
                <Search size={18} color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search applicant name, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="lg"
              />
            </InputGroup>

            {/* Filter Pills */}
            <HStack spacing={4} wrap="wrap" justify="end" w={{ base: 'full', md: 'auto' }}>
              <Select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                maxW="150px"
                borderRadius="lg"
              >
                <option value="ALL">All Roles</option>
                <option value="DRIVER">Driver</option>
                <option value="CONDUCTOR">Conductor</option>
              </Select>

              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                maxW="180px"
                borderRadius="lg"
              >
                <option value="ALL">All Statuses</option>
                <option value="SUBMITTED">Pending Review</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="APPROVED">Approved</option>
                <option value="HIRED">Hired</option>
                <option value="REJECTED">Rejected</option>
                <option value="DRAFT">Draft</option>
                <option value="WITHDRAWN">Withdrawn</option>
              </Select>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Main List Table */}
      <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" overflow="hidden">
        {isAppsLoading ? (
          <Center py={16}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        ) : appsError ? (
          <Center py={16} flexDir="column">
            <AlertCircle size={40} color="red" />
            <Text mt={4} fontWeight="medium">Failed to load applications. Please try again.</Text>
          </Center>
        ) : filteredApps.length === 0 ? (
          <Center py={16} flexDir="column">
            <FileText size={48} color="gray" />
            <Text mt={4} fontWeight="medium" color="gray.500">No applications match your filter criteria.</Text>
          </Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead bg={useColorModeValue('gray.50', 'gray.900')}>
                <Tr>
                  <Th>Applicant</Th>
                  <Th>Role</Th>
                  <Th>Experience</Th>
                  <Th>License No.</Th>
                  <Th>Applied Date</Th>
                  <Th>Status</Th>
                  <Th textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredApps.map((app: DriverApplication) => {
                  const applicantName = app.applicant ? `${app.applicant.firstName} ${app.applicant.lastName}` : 'Unknown'
                  return (
                    <Tr key={app.id} _hover={{ bg: hoverBg }} transition="background 0.2s">
                      <Td py={4}>
                        <HStack spacing={3}>
                          <Avatar size="sm" name={applicantName} />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold" fontSize="sm">{applicantName}</Text>
                            <Text fontSize="xs" color="gray.500">{app.applicant?.phoneNumber}</Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <Tag size="sm" colorScheme={app.roleAppliedFor === 'DRIVER' ? 'blue' : 'orange'} borderRadius="full">
                          <TagLabel fontWeight="bold">{app.roleAppliedFor}</TagLabel>
                        </Tag>
                      </Td>
                      <Td>
                        <Text fontSize="sm" fontWeight="medium">{app.yearsOfExperience} years</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" fontFamily="mono">{app.licenseNumber || 'N/A'}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.500">{new Date(app.createdAt).toLocaleDateString()}</Text>
                      </Td>
                      <Td>{getStatusBadge(app.status)}</Td>
                      <Td textAlign="right">
                        <HStack spacing={2} justify="end">
                          <IconButton
                            aria-label="View Details"
                            icon={<Eye size={16} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenView(app)}
                            title="View details"
                          />
                          {['SUBMITTED', 'UNDER_REVIEW'].includes(app.status) && (
                            <Button
                              size="sm"
                              colorScheme="purple"
                              variant="outline"
                              onClick={() => handleOpenReview(app)}
                              leftIcon={<Clock size={14} />}
                            >
                              Review
                            </Button>
                          )}
                          {app.status === 'APPROVED' && (
                            <Button
                              size="sm"
                              colorScheme="green"
                              onClick={() => handleOpenHire(app)}
                              leftIcon={<UserCheck size={14} />}
                            >
                              Hire
                            </Button>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Pagination Footer */}
        {!isAppsLoading && !appsError && totalPages > 1 && (
          <Flex justify="space-between" align="center" px={6} py={4} borderTop="1px" borderColor={borderColor}>
            <Text fontSize="sm" color="gray.500">
              Showing page {currentPage} of {totalPages}
            </Text>
            <HStack spacing={2}>
              <IconButton
                aria-label="Previous Page"
                icon={<ChevronLeft size={18} />}
                size="sm"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              />
              <IconButton
                aria-label="Next Page"
                icon={<ChevronRight size={18} />}
                size="sm"
                isDisabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              />
            </HStack>
          </Flex>
        )}
      </Card>

      {/* Modal 1: Full Details View */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" maxW="4xl">
          <ModalHeader borderBottom="1px" borderColor={borderColor} pb={4}>
            <Flex justify="space-between" align="center" wrap="wrap" pr={8}>
              <HStack spacing={4}>
                <Avatar name={selectedApp?.applicant ? `${selectedApp.applicant.firstName} ${selectedApp.applicant.lastName}` : ''} size="md" />
                <VStack align="start" spacing={0}>
                  <Heading size="md">{selectedApp?.applicant ? `${selectedApp.applicant.firstName} ${selectedApp.applicant.lastName}` : 'Application Details'}</Heading>
                  <Text fontSize="sm" color="gray.500">{selectedApp?.applicant?.email} • {selectedApp?.applicant?.phoneNumber}</Text>
                </VStack>
              </HStack>
              {selectedApp && getStatusBadge(selectedApp.status)}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={6}>
            {selectedApp && (
              <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={8}>
                {/* Left Side: General Profile and Background */}
                <GridItem>
                  <VStack align="stretch" spacing={6}>
                    {/* General Profile Section */}
                    <Box>
                      <Heading size="xs" textTransform="uppercase" color="blue.500" mb={3}>Personal Details</Heading>
                      <SimpleGrid columns={2} spacing={4}>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="bold">Role Applied For</Text>
                          <Text fontSize="sm" fontWeight="semibold">{selectedApp.roleAppliedFor}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="bold">Address</Text>
                          <Text fontSize="sm">{selectedApp.address}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="bold">Date of Birth</Text>
                          <Text fontSize="sm">{selectedApp.dateOfBirth ? new Date(selectedApp.dateOfBirth).toLocaleDateString() : 'N/A'}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="bold">Gender</Text>
                          <Text fontSize="sm">{selectedApp.gender || 'N/A'}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="bold">Years of Experience</Text>
                          <Text fontSize="sm">{selectedApp.yearsOfExperience} Years</Text>
                        </Box>
                      </SimpleGrid>
                    </Box>

                    <Divider borderColor={borderColor} />

                    {/* License Section */}
                    {selectedApp.roleAppliedFor === 'DRIVER' && (
                      <Box>
                        <Heading size="xs" textTransform="uppercase" color="blue.500" mb={3}>Driving Credentials</Heading>
                        <SimpleGrid columns={2} spacing={4}>
                          <Box>
                            <Text fontSize="xs" color="gray.500" fontWeight="bold">License Number</Text>
                            <Text fontSize="sm" fontFamily="mono" fontWeight="medium">{selectedApp.licenseNumber || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" fontWeight="bold">License Expiry</Text>
                            <Text fontSize="sm" color={selectedApp.licenseExpiry && new Date(selectedApp.licenseExpiry) < new Date() ? 'red.500' : 'inherit'}>
                              {selectedApp.licenseExpiry ? new Date(selectedApp.licenseExpiry).toLocaleDateString() : 'N/A'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" fontWeight="bold">License Classes</Text>
                            <HStack spacing={1} mt={1}>
                              {selectedApp.licenseClasses && selectedApp.licenseClasses.length > 0 ? (
                                selectedApp.licenseClasses.map((cls, idx) => (
                                  <Badge key={idx} colorScheme="teal" variant="subtle">{cls}</Badge>
                                ))
                              ) : (
                                <Text fontSize="sm">None</Text>
                              )}
                            </HStack>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    )}

                    {selectedApp.roleAppliedFor === 'DRIVER' && <Divider borderColor={borderColor} />}

                    {/* Recommenders Section */}
                    <Box>
                      <Heading size="xs" textTransform="uppercase" color="blue.500" mb={3}>Recommenders</Heading>
                      {selectedApp.recommenders && selectedApp.recommenders.length > 0 ? (
                        <List spacing={3}>
                          {selectedApp.recommenders.map((rec) => (
                            <ListItem key={rec.id} p={3} border="1px" borderColor={borderColor} borderRadius="lg">
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="semibold" fontSize="sm">{rec.fullName}</Text>
                                  <Text fontSize="xs" color="gray.500">{rec.relationship} {rec.organization ? `at ${rec.organization}` : ''}</Text>
                                </VStack>
                                <VStack align="end" spacing={0}>
                                  <Text fontSize="xs" color="gray.600"><Phone size={12} style={{ display: 'inline', marginRight: '4px' }} /> {rec.phoneNumber}</Text>
                                  {rec.email && <Text fontSize="xs" color="gray.600"><Mail size={12} style={{ display: 'inline', marginRight: '4px' }} /> {rec.email}</Text>}
                                </VStack>
                              </HStack>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Text fontSize="sm" color="gray.500" fontStyle="italic">No recommenders provided.</Text>
                      )}
                    </Box>
                  </VStack>
                </GridItem>

                {/* Right Side: Documents and Review status */}
                <GridItem borderLeft={{ md: '1px' }} borderColor={{ md: borderColor }} pl={{ md: 6 }}>
                  <VStack align="stretch" spacing={6}>
                    {/* Documents */}
                    <Box>
                      <Heading size="xs" textTransform="uppercase" color="blue.500" mb={4}>Verification Docs</Heading>
                      <VStack align="stretch" spacing={3}>
                        {selectedApp.nationalIdUrl && (
                          <Button
                            as="a"
                            href={selectedApp.nationalIdUrl}
                            target="_blank"
                            size="sm"
                            variant="outline"
                            leftIcon={<FileText size={16} />}
                            justifyContent="start"
                          >
                            National ID
                          </Button>
                        )}
                        {selectedApp.passportUrl && (
                          <Button
                            as="a"
                            href={selectedApp.passportUrl}
                            target="_blank"
                            size="sm"
                            variant="outline"
                            leftIcon={<FileText size={16} />}
                            justifyContent="start"
                          >
                            Passport Photo
                          </Button>
                        )}
                        {selectedApp.cvUrl && (
                          <Button
                            as="a"
                            href={selectedApp.cvUrl}
                            target="_blank"
                            size="sm"
                            variant="outline"
                            leftIcon={<FileText size={16} />}
                            justifyContent="start"
                          >
                            Curriculum Vitae (CV)
                          </Button>
                        )}
                        {selectedApp.licenseUrl && (
                          <Button
                            as="a"
                            href={selectedApp.licenseUrl}
                            target="_blank"
                            size="sm"
                            variant="outline"
                            leftIcon={<FileText size={16} />}
                            justifyContent="start"
                          >
                            Driving License Copy
                          </Button>
                        )}
                        {!selectedApp.nationalIdUrl && !selectedApp.passportUrl && !selectedApp.cvUrl && !selectedApp.licenseUrl && (
                          <Text fontSize="sm" color="gray.500">No documents uploaded.</Text>
                        )}
                      </VStack>
                    </Box>

                    <Divider borderColor={borderColor} />

                    {/* Review Log */}
                    <Box>
                      <Heading size="xs" textTransform="uppercase" color="blue.500" mb={3}>Review Timeline</Heading>
                      <VStack align="stretch" spacing={3}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">Status</Text>
                          <Text fontSize="sm" fontWeight="bold">{selectedApp.status}</Text>
                        </Box>
                        {selectedApp.reviewedAt && (
                          <Box>
                            <Text fontSize="xs" color="gray.500">Reviewed At</Text>
                            <Text fontSize="sm">{new Date(selectedApp.reviewedAt).toLocaleString()}</Text>
                          </Box>
                        )}
                        {selectedApp.reviewNotes && (
                          <Box>
                            <Text fontSize="xs" color="gray.500">Review Notes</Text>
                            <Text fontSize="sm" bg={useColorModeValue('gray.50', 'gray.900')} p={2} borderRadius="md" border="1px" borderColor={borderColor}>
                              {selectedApp.reviewNotes}
                            </Text>
                          </Box>
                        )}
                        {selectedApp.rejectionReason && (
                          <Box>
                            <Text fontSize="xs" color="red.500">Rejection Reason (Public)</Text>
                            <Text fontSize="sm" color="red.600" bg="red.50" p={2} borderRadius="md" border="1px" borderColor="red.100">
                              {selectedApp.rejectionReason}
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  </VStack>
                </GridItem>
              </Grid>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor={borderColor} gap={2}>
            <Button variant="ghost" onClick={() => setIsViewOpen(false)}>Close</Button>
            {selectedApp && ['SUBMITTED', 'UNDER_REVIEW'].includes(selectedApp.status) && (
              <Button colorScheme="purple" onClick={() => { setIsViewOpen(false); handleOpenReview(selectedApp); }}>
                Review Application
              </Button>
            )}
            {selectedApp && selectedApp.status === 'APPROVED' && (
              <Button colorScheme="green" onClick={() => { setIsViewOpen(false); handleOpenHire(selectedApp); }}>
                Hire & Assign Bus
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal 2: Review Modal (Status & Notes) */}
      <Modal isOpen={isReviewOpen} onClose={() => setIsReviewOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Review Driver Application</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Application Status</FormLabel>
                <Select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value as any)}>
                  <option value="UNDER_REVIEW">Move to Under Review</option>
                  <option value="APPROVED">Approve Application</option>
                  <option value="REJECTED">Reject Application</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Reviewer Notes (Internal)</FormLabel>
                <Textarea
                  placeholder="Private internal evaluation notes..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                />
              </FormControl>

              {reviewStatus === 'REJECTED' && (
                <FormControl isRequired>
                  <FormLabel color="red.500">Reason for Rejection (Applicant Visible)</FormLabel>
                  <Textarea
                    placeholder="Provide a reason for the rejection that will be shared with the applicant via SMS and notifications..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                </FormControl>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setIsReviewOpen(false)}>Cancel</Button>
            <Button
              colorScheme={reviewStatus === 'APPROVED' ? 'green' : reviewStatus === 'REJECTED' ? 'red' : 'purple'}
              onClick={handleReviewSubmit}
              isLoading={reviewMutation.isLoading}
            >
              Submit Review
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal 3: Hire Modal (Assign Bus) */}
      <Modal isOpen={isHireOpen} onClose={() => setIsHireOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Complete Hiring Onboarding</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box bg="green.50" color="green.800" p={4} borderRadius="lg" border="1px" borderColor="green.200">
                <Text fontWeight="semibold">Hiring {selectedApp?.applicant ? `${selectedApp.applicant.firstName} ${selectedApp.applicant.lastName}` : ''}</Text>
                <Text fontSize="sm" mt={1}>This will promote the applicant user account to an active crew member role ({selectedApp?.roleAppliedFor}). You must assign an initial bus (Daladala) below to complete onboarding.</Text>
              </Box>

              <FormControl isRequired>
                <FormLabel>Select Daladala (Bus)</FormLabel>
                <Select
                  placeholder="Select a registration number..."
                  value={assignedBusId}
                  onChange={(e) => setAssignedBusId(e.target.value)}
                >
                  {vehiclesData?.data?.map((vehicle: any) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.registrationNumber} ({vehicle.make || 'Generic'} {vehicle.model || ''})
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setIsHireOpen(false)}>Cancel</Button>
            <Button
              colorScheme="green"
              onClick={handleHireSubmit}
              isLoading={hireMutation.isLoading}
              isDisabled={!assignedBusId}
            >
              Hire Crew Member
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
